import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { forkJoin, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { ComparatorService } from '@adgate/common/services/comparator-service';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { CommercialOffice } from '@adgate/common/types/commercial-office.type';
import { Agency } from '@adgate/common/types/agency.type';
import { BaseObject } from '@adgate/common/types/base-object.type';
import { User } from '@adgate/modules/admin/types/user.type';
import { Campaign } from '@adgate/modules/streaming/types/campaign.type';
import { CustomValidators } from '@adgate/common/validators/custom-validators.validator';

@Component({
  selector: 'app-streaming-campaign-edit-dialog-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatAutocompleteModule,
    ObjectDescPipe
  ],
  templateUrl: './streaming-campaign-edit-dialog-component.html',
  styleUrl: './streaming-campaign-edit-dialog-component.scss',
})
export class StreamingCampaignEditDialogComponent implements OnInit
{
  private dialogRef: MatDialogRef<StreamingCampaignEditDialogComponent> = inject(MatDialogRef<StreamingCampaignEditDialogComponent>);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogService: DialogService = inject(DialogService);
  private commonObjectService: CommonObjectService = inject(CommonObjectService);
  private authService: AuthenticationService = inject(AuthenticationService);
  public data: Campaign = inject(MAT_DIALOG_DATA, { optional: false });

  public comparatorService: ComparatorService = inject(ComparatorService);

  campaignForm: FormGroup;
  commercialOffices: CommercialOffice[] = [];
  agencies: Agency[] = [];
  brands: string[] = [];
  filteredBrands!: Observable<string[]>;

  constructor()
  {
    this.campaignForm = this.fb.group({
      commercialOffice: ['', Validators.required],
      agency: ['', Validators.required],
      campaignTitle: ['', [Validators.required,CustomValidators.invalidCharactersValidator()]],
      brandAdvertiser: ['', [Validators.required,CustomValidators.invalidCharactersValidator()]],
      targetUrl: ['']
    });
  }

  ngOnInit(): void
  {
    let userAttributes = this.authService.getUserAttributes();
    let officeFilter = null;
    let agencyFilter = null;

    if (userAttributes.type == User.TYPE_OFFICE) {
      agencyFilter = userAttributes.office.id;
    } else if (userAttributes.type == User.TYPE_AGENCY) {
      officeFilter = userAttributes.agency.id;
    }

    this.dialogService.showProgress();

    forkJoin([
      this.commonObjectService.getCommercialOffices(officeFilter),
      this.commonObjectService.getAgencies(agencyFilter)
    ]).subscribe({
      next: (responses: RESTApplicationResponse[]) => {
        this.commercialOffices = responses[0].data;
        this.agencies = responses[1].data;

        this.initializeUserProfile(userAttributes);

        if (this.data.id != -1) {
          this.campaignForm.get('commercialOffice')?.setValue(this.data.commercialOffice);
          this.campaignForm.get('agency')?.setValue(this.data.agency);
          this.campaignForm.get('campaignTitle')?.setValue(this.data.title);
          this.campaignForm.get('brandAdvertiser')?.setValue(this.data.brand);
          this.campaignForm.get('targetUrl')?.setValue(this.data.targetUrl);
        }

        this.dialogService.hideProgress();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  initializeUserProfile(userAttributes: any): void
  {
    if (userAttributes.type == User.TYPE_OFFICE) {
      this.campaignForm.get('commercialOffice')?.setValue(userAttributes.office);
      this.campaignForm.get('commercialOffice')?.disable();
    } else if (userAttributes.type == User.TYPE_AGENCY) {
      this.campaignForm.get('agency')?.setValue(userAttributes.agency);
      this.campaignForm.get('agency')?.disable();
      this.onChangeAgency(userAttributes.agency);
    }
  }

  onChangeCommercialOffice(office: BaseObject): void
  {
    this.commonObjectService.getAgencies(office.id).subscribe({
      next: (response: RESTApplicationResponse) => {
        this.agencies = response.data;
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onChangeAgency(agency: BaseObject): void
  {
    this.commonObjectService.getBrands(agency.id).subscribe({
      next: (response: RESTApplicationResponse) => {
        this.brands = response.data;

        this.filteredBrands = this.campaignForm.get('brandAdvertiser')!.valueChanges.pipe(
          startWith(''),
          map(value => this._filterBrands(value || ''))
        );
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  private _filterBrands(value: string): string[]
  {
    const filterValue = value.toLowerCase();
    return this.brands.filter(brand => brand.toLowerCase().includes(filterValue));
  }

  onCancel(): void
  {
    this.dialogRef.close(null);
  }

  onSubmit(): void
  {
    if (this.campaignForm.valid) {
      this.data.commercialOffice = this.campaignForm.get('commercialOffice')?.value;
      this.data.agency = this.campaignForm.get('agency')?.value;
      this.data.title = this.campaignForm.get('campaignTitle')?.value;
      this.data.brand = this.campaignForm.get('brandAdvertiser')?.value;
      this.data.targetUrl = this.campaignForm.get('targetUrl')?.value;

      this.dialogRef.close(this.data);
    }
  }
}
